import type { AccessInvite } from './api';

/** Statuses that mean the invite is no longer live. */
export const DEAD_INVITE_STATUSES = new Set([
  'cancelled',
  'canceled',
  'expired',
  'revoked',
  'declined',
  'rejected',
]);

export interface UpcomingInviteFilterOptions {
  /** When false, hides accepted invites (home widget). Default true (access list). */
  includeAccepted?: boolean;
  limit?: number;
}

/**
 * Invites that are still in play — not cancelled/expired and (optionally) not
 * yet accepted. Sorted by visit start.
 */
export function filterUpcomingInvites(
  invites: AccessInvite[],
  options?: UpcomingInviteFilterOptions,
): AccessInvite[] {
  const now = Date.now();
  const includeAccepted = options?.includeAccepted ?? true;

  const sorted = [...invites]
    .filter((invite) => {
      const status = (invite.status ?? '').toLowerCase();
      if (DEAD_INVITE_STATUSES.has(status)) return false;
      if (!includeAccepted && (status === 'accepted' || invite.accepted_at)) return false;
      if (invite.valid_until) {
        const end = new Date(invite.valid_until).getTime();
        if (!Number.isNaN(end) && end < now) return false;
      }
      return true;
    })
    .sort((a, b) => (a.valid_from ?? '').localeCompare(b.valid_from ?? ''));

  const limit = options?.limit;
  return limit != null ? sorted.slice(0, limit) : sorted;
}

/** A PIN can be cancelled while it is still live, including once the visitor has accepted it. */
export function isCancellableInvite(invite: AccessInvite): boolean {
  return !DEAD_INVITE_STATUSES.has((invite.status ?? '').toLowerCase());
}
